import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import AudioModal from "../../../../../common/AudioModal";
import ConfirmationModal from "../../../../../common/ConfirmationModal";
import QTDetailsAudioWithLabelSection from "../../../../../common/QTDetailsAudioWithLabelSection";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import { Toast } from "../../../../../common/alert";
import { mainLayoutStyles, showNotification } from "../../../../../constants";
import { useAuth } from "../../../../../hooks";

import {
  getSingleClassroomAudioReaction,
  updateClassroomAudioReaction,
} from "../../hooks/GAClassroomAudioReactionsHooks";

const isFirestoreTimestamp = (value) =>
  value && typeof value === "object" && typeof value?.seconds === "number";

const GAClassroomAudioReactionDetailsContainer = () => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);

  const [details, setDetails] = useState(
    location?.state?.classroomAudioReaction?.id
      ? location.state.classroomAudioReaction
      : {}
  );

  const [audioModal, setAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const pendingProcess = useRef(false);

  const fetchDetails = async () => {
    setIsLoadingData(true);
    try {
      if (!id || id === ":id") {
        navigate("/classroom-audio-reactions", { replace: true });
        return;
      }

      const docSnap = await getSingleClassroomAudioReaction(id);
      if (!docSnap.exists()) {
        navigate("/classroom-audio-reactions", { replace: true });
        return;
      }
      setDetails({ id: docSnap.id, ...docSnap.data() });
    } catch (e) {
      console.error("Error loading classroom audio reaction:", e);
      navigate("/classroom-audio-reactions", { replace: true });
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (location?.state?.classroomAudioReaction?.id) {
      setDetails(location.state.classroomAudioReaction);
    } else {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fieldsToRender = useMemo(() => {
    if (!details) return [];

    const entries = Object.entries(details);
    const audioKeys = new Set(["audioUrl", "processedAudioUrl"]);

    return entries
      .filter(([k]) => !audioKeys.has(k))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [details]);

  const handleAudioModalOpen = (url) => {
    if (!url) return;
    setSelectedAudioFile(url);
    setAudioModal(true);
  };

  const handleProcessOriginalAudio = () => {
    if (!editEnabled) return;
    setConfirmationModalOpen(true);
  };

  const handleProcessAudio = async () => {
    setConfirmationModalOpen(false);

    if (!details?.audioUrl) {
      Toast("error", "No original audio file to process.");
      return;
    }

    setLoading(true);
    pendingProcess.current = true;
    try {
      const functions = getFunctions();
      const processAudio = httpsCallable(functions, "processAudioWithAuphonicV3");

      const result = await processAudio({
        fileUrl: details.audioUrl,
        qtTitle: details.id,
        quicktaleId: details.id,
        ogFileName: details.audioName || details.originalAudioName || details.id,
        isReaction: true,
      });

      const { success, filePath, processedAudioName, error } = result.data || {};
      if (!success) {
        throw new Error(error || "Failed to process audio.");
      }

      // compute duration
      const audio = new Audio(filePath);
      const duration = await new Promise((resolve) => {
        audio.onloadedmetadata = () => resolve(Math.floor(audio.duration));
        audio.onerror = () => resolve(undefined);
      });

      setDetails((prev) => ({
        ...prev,
        processedAudioUrl: filePath,
        processedAudioName: processedAudioName || prev.processedAudioName,
        audioLength: duration ?? prev.audioLength,
      }));

      showNotification("Audio successfully processed.");
    } catch (e) {
      console.error("Error processing audio:", e);
      Toast("error", `Error processing audio: ${e.message}`);
    } finally {
      setLoading(false);
      pendingProcess.current = false;
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const dataTemp = {
        processedAudioUrl: details?.processedAudioUrl || "",
        processedAudioName: details?.processedAudioName || "",
        audioLength: details?.audioLength || 0,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
      };

      await updateClassroomAudioReaction(details.id, dataTemp);
      showNotification("Classroom audio reaction updated successfully.");
      setEditEnabled(false);

      // refresh from server to reflect any server-side transforms
      await fetchDetails();
    } catch (e) {
      console.error("Error saving classroom audio reaction:", e);
      Toast("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CssBaseline />
        <GeneralAdminSidebar />
        <Grid
          container
          style={{
            ...mainLayoutStyles,
            background: "#fff",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Grid>
      </Box>
    );
  }

  if (!details?.id) return null;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"Classroom Audio Reaction Details"}
        bgColor="#fff"
        buttonText={currentUser?.role === "moderator" ? undefined : "Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        handleSave={handleSave}
        loading={loading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          <Box style={dataContainerStyles}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <QTDetailsAudioWithLabelSection
                  label={"Original Audio File"}
                  handleAudioModalOpen={() => handleAudioModalOpen(details?.audioUrl)}
                  editEnabled={editEnabled}
                  details={details}
                  setDetails={setDetails}
                  audioNameField={"audioName"}
                  audioUrlField={"audioUrl"}
                  hideUploadButton={true}
                  showProcessButton={editEnabled}
                  onProcessAudio={handleProcessOriginalAudio}
                />

                <QTDetailsAudioWithLabelSection
                  label={"Processed Audio File"}
                  handleAudioModalOpen={() =>
                    handleAudioModalOpen(details?.processedAudioUrl)
                  }
                  editEnabled={false}
                  details={details}
                  setDetails={setDetails}
                  audioNameField={"processedAudioName"}
                  audioUrlField={"processedAudioUrl"}
                  hideUploadButton={true}
                />

                <QTDetailsTextFieldWithLabelSection
                  label={"Reaction ID"}
                  value={details?.id}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                {fieldsToRender.map(([key, value]) => {
                  let displayValue = value;

                  if (isFirestoreTimestamp(value)) {
                    displayValue = new Date(value.seconds * 1000).toLocaleString();
                  } else if (typeof value === "object" && value !== null) {
                    // Keep it readable; this is GA tooling so JSON is acceptable.
                    try {
                      displayValue = JSON.stringify(value);
                    } catch {
                      displayValue = String(value);
                    }
                  }

                  return (
                    <QTDetailsTextFieldWithLabelSection
                      key={key}
                      label={key}
                      value={displayValue ?? ""}
                      disabled={true}
                    />
                  );
                })}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      <AudioModal
        modalOpen={audioModal}
        handleClose={() => setAudioModal(false)}
        audioFile={selectedAudioFile}
      />

      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={handleProcessAudio}
        title="Process Audio"
        message="Would you like to clean and normalize audio?"
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GAClassroomAudioReactionDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};

const dataContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "24px 15px",
  marginBottom: 30,
};
